import React, { useEffect, useRef, useState } from 'react';
import { extractPeaks, seedBars } from './audioUtils';

const ACCENT = '#BFA253';
const BAR_COUNT = 58;

const formatTime = (seconds) => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const mins = Math.floor(safe / 60);
  const secs = (safe % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

const OnsiteWaveformPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [bars, setBars] = useState(() => seedBars(src));

  // Decode real waveform on src change
  useEffect(() => {
    if (!src) return undefined;
    setBars(seedBars(src));
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    let cancelled = false;
    extractPeaks(src)
      .then((peaks) => { if (!cancelled) setBars(peaks); })
      .catch(() => { /* CORS or decode fail — keep seeded fallback */ });
    return () => { cancelled = true; };
  }, [src]);

  // Wire audio element events (once on mount)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onMeta = () => {
      const d = audio.duration;
      setDuration(Number.isFinite(d) && d > 0 ? d : 0);
    };
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };

    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try { await audio.play(); } catch (_) { /* ignore */ }
    } else {
      audio.pause();
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  const played = Math.round(progress * BAR_COUNT);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
      <audio ref={audioRef} src={src} preload="auto" style={{ display: 'none' }} />

      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        style={{
          width: '28px', height: '28px', borderRadius: '50%',
          border: '1px solid #d6d6d6', background: '#fff',
          color: '#666', cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <i className={`fa-solid fa-${isPlaying ? 'pause' : 'play'}`} style={{ fontSize: 11 }} />
      </button>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          onClick={handleSeek}
          style={{
            height: '48px', display: 'flex', alignItems: 'center', gap: '3px',
            cursor: duration > 0 ? 'pointer' : 'default',
            padding: '0 4px',
          }}
        >
          {bars.map((h, i) => (
            <span
              key={i}
              style={{
                flex: '0 0 auto', width: '3px',
                height: `${Math.round(44 * h)}px`,
                borderRadius: '3px',
                background: i < played ? ACCENT : '#c5c5c5',
                transition: 'background 0.1s',
              }}
            />
          ))}
        </div>

        <span style={{ fontSize: '11px', color: '#888', whiteSpace: 'nowrap', fontFamily: '"Poppins", sans-serif', flexShrink: 0 }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
    </div>
  );
};

export default OnsiteWaveformPlayer;
